import React from 'react';
import JobCategoryPage from './JobCategoryPage';
import { Stethoscope } from 'lucide-react';

const MedicalSocialWorkerJobs = () => {
  const contentSections = [
    {
      title: "What is a Medical Social Worker?",
      content: (
        <p>
          Medical social workers are licensed professionals who provide psychosocial support to patients in various healthcare settings beyond acute hospital care. Working in dialysis centers, skilled nursing facilities, rehabilitation centers, assisted living communities, and VA clinics, these professionals help patients and families navigate the complex medical system while addressing social, emotional, and practical needs that affect health outcomes.
        </p>
      )
    },
    {
      title: "Medical Social Worker Job Responsibilities",
      content: (
        <p>
          Medical social workers serve as advocates and coordinators across diverse healthcare environments. Their responsibilities vary by setting but typically include comprehensive patient assessments, care coordination, and family support services.
        </p>
      ),
      list: [
        "Conduct psychosocial assessments for patients in medical settings",
        "Coordinate transitions of care between facilities",
        "Connect patients with community resources and support services",
        "Facilitate family meetings and care planning conferences",
        "Provide counseling for adjustment to chronic illness",
        "Assist with insurance navigation and financial counseling",
        "Document clinical interventions in electronic health records",
        "Collaborate with interdisciplinary healthcare teams",
        "Support advance care planning discussions"
      ]
    },
    {
      title: "Common Medical Social Work Settings",
      content: (
        <p>
          Medical social workers find employment opportunities across numerous healthcare settings, each with unique patient populations and care challenges.
        </p>
      ),
      list: [
        "Dialysis centers and renal care clinics",
        "Skilled nursing facilities (SNFs)",
        "Rehabilitation hospitals and centers",
        "Assisted living and memory care communities",
        "VA hospitals and clinics",
        "Outpatient specialty clinics",
        "Long-term acute care hospitals (LTACHs)",
        "Home health agencies"
      ]
    },
    {
      title: "Required Qualifications and Licenses",
      content: (
        <p>
          Medical social work positions typically require a Master of Social Work (MSW) degree, though some settings accept LBSW practitioners for specific roles. State licensure requirements vary, with most positions requiring LMSW or LCSW credentials depending on the level of clinical responsibility.
        </p>
      ),
      list: [
        "Master of Social Work (MSW) from a CSWE-accredited program preferred",
        "State clinical licensure (LCSW, LMSW, or LBSW)",
        "Knowledge of medical terminology and healthcare systems",
        "Experience with chronic illness populations",
        "Strong documentation and communication skills",
        "Understanding of Medicare/Medicaid regulations"
      ]
    },
    {
      title: "Salary and Career Growth",
      content: (
        <p>
          Medical social workers earn competitive salaries ranging from $45,000 to $75,000+ depending on setting, location, and experience. Specialized facilities like dialysis centers and rehabilitation hospitals often offer higher compensation. Career advancement opportunities include lead social worker positions, department management, and regional coordinator roles. The growing aging population and expansion of outpatient services suggest strong job security in this specialty.
        </p>
      )
    }
  ];

  const relatedCategories = [
    { name: "Hospital Social Worker Jobs", href: "/hospital-social-worker-jobs" },
    { name: "Hospice Social Worker Jobs", href: "/hospice-social-worker-jobs" },
    { name: "Clinical Social Worker Jobs", href: "/clinical-social-worker-jobs" },
    { name: "Community Social Worker Jobs", href: "/community-social-worker-jobs" },
  ];

  return (
    <JobCategoryPage
      category="medical-social-worker-jobs"
      title="Medical Social Worker Jobs"
      metaTitle="Medical Social Worker Jobs | Dialysis, SNF, Rehab Positions | INeedASocialWorker"
      metaDescription="Find medical social worker jobs in dialysis centers, skilled nursing facilities, rehab centers, and VA clinics. LCSW and LMSW positions available. Apply with SW Express Apply™."
      heroTitle="Medical Social Worker Jobs"
      heroSubtitle="Find rewarding medical social work positions at dialysis centers, skilled nursing facilities, rehabilitation centers, and healthcare organizations."
      searchKeywords={["medical social worker", "dialysis social worker", "SNF social worker", "skilled nursing social worker", "rehab social worker", "VA social worker"]}
      contentSections={contentSections}
      relatedCategories={relatedCategories}
      iconComponent={Stethoscope}
    />
  );
};

export default MedicalSocialWorkerJobs;
